import { AxiosResponse } from 'axios';

import API from '../Api';
import { getUserToken, logUserOut } from '../utils/login';
import { Customer, UpdateInfoForm, DetailedChildUser, CustomerRoleChild, CustomerProperty } from '../types/customer';

interface Parameters {
  CustomerId?: string | number;
  MobileNumber?: string;
}

export const getCustomerDetails = async (dataOwnerCustomerId: number, actingCustomerId: number): Promise<Customer | undefined> => {
  const token = sessionStorage.getItem('token');

  try {
    const customerData: AxiosResponse<Customer> = await API({
      method: 'post',
      url: `/v1/customer/view/${dataOwnerCustomerId}/${actingCustomerId}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: {
        content: '',
        version: 2,
      },
    });

    return customerData.data;
  } catch {}

  return undefined;
};

export const postCustomerDetails = async (
  dataOwnerCustomerId: number,
  actingCustomerId: number,
  updateDetails: UpdateInfoForm,
): Promise<Customer | []> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  const customerData: AxiosResponse<Customer> = await API({
    method: 'post',
    url: `/v1/customer/update/${dataOwnerCustomerId}/${actingCustomerId}`,
    headers: {
      Authorization: `Bearer ${bearer}`,
    },
    data: { updateDetails, content },
    validateStatus: function (status) {
      return status === 200 || status === 404;
    },
  });

  return customerData.data;
};

export const getChildCustomerDetails = async (dataOwnerCustomerId: number): Promise<DetailedChildUser> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  const childCustomerData: AxiosResponse<DetailedChildUser> = await API({
    method: 'post',
    url: `/v1/customerChild/${dataOwnerCustomerId}`,
    headers: {
      Authorization: `Bearer ${bearer}`,
    },
    data: { content },
  }).catch((error) => {
    if (error.response) {
      if (error.response.status === 401) {
        logUserOut();
      }
      return error.response;
    }
  });

  return childCustomerData.data;
};

export const getCustomers = async (dataOwnerCustomerId: number, actingCustomerId: number, parameters: Parameters): Promise<Customer[] | []> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }
  try {
    const customers = await API({
      method: 'post',
      url: `/v1/searchCustomers/ret/${dataOwnerCustomerId}/${actingCustomerId}/search`,
      headers: {
        Authorization: `Bearer ${bearer}`,
      },
      data: { customerId: dataOwnerCustomerId, parameters, content },
    }).catch((error) => {
      if (error.response) {
        if (error.response.status === 401) {
          logUserOut();
        }
        return error.response;
      }
    });

    if (customers.data[0].mobileNumber === parameters.MobileNumber) {
      return customers.data;
    }
  } catch {}

  return [];
};

export const postCustomerRoleChilds = async (
  customerId: number,
  customerRoleTypeNo: number,
  targetCustomerId: number,
): Promise<CustomerRoleChild[] | boolean> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }
  try {
    const date = new Date();
    const requestData = {
      customerRoleType: {
        customerRoleTypeNo: 83,
        customerRoleTypeName: 'parent role',
      },
      customerIdChild: targetCustomerId,
      customerRoleTypeChild: {
        customerRoleTypeNo: 82,
        customerRoleTypeName: 'child role',
      },
      FromDateChild: date.toISOString(),
    };
    await API({
      method: 'post',
      url: `/v1/postCustomerRoleChilds/ret/post`,
      headers: {
        Authorization: `Bearer ${bearer}`,
      },
      data: { customerId, customerRoleTypeNo, requestData, content },
    });

    return true;
  } catch {}

  return false;
};

export const deleteCustomerRoleChilds = async (customerId: number, customerRoleId: number, targetCustomerId: number): Promise<unknown | boolean> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }
  try {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    const requestData = {
      customerRoleType: {
        customerRoleTypeNo: 83,
        customerRoleTypeName: 'parent role',
      },
      customerIdChild: targetCustomerId,
      customerRoleTypeChild: {
        customerRoleTypeNo: 82,
        customerRoleTypeName: 'child role',
      },
      FromDateChild: date.toISOString(),
      ToDateChild: date.toISOString(),
    };
    await API({
      method: 'post',
      url: `/v1/putCustomerRoleChilds/ret/put`,
      headers: {
        Authorization: `Bearer ${bearer}`,
      },
      data: { customerId, customerRoleId, requestData, content },
    });

    return true;
  } catch {}

  return false;
};

export const searchCustomerRoleChilds = async (customerId: number, parameters: Parameters): Promise<[]> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }
  try {
    const customers = await API({
      method: 'post',
      url: `/v1/searchCustomerRoleChilds/ret/search`,
      headers: {
        Authorization: `Bearer ${bearer}`,
      },
      data: { customerId, parameters, content },
    });

    return customers.data;
  } catch {}

  return [];
};

export const getCustomerNumberLSM = async (dataOwnerCustomerId: number): Promise<CustomerProperty[] | undefined> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }
  try {
    const customerData: AxiosResponse<CustomerProperty[]> = await API({
      method: 'post',
      url: `/v1/customerNumberLSM/${dataOwnerCustomerId}`,
      headers: {
        Authorization: `Bearer ${bearer}`,
      },
      data: { content },
    }).catch((error) => {
      if (error.response) {
        if (error.response.status === 401) {
          logUserOut();
        }
        return error.response;
      }
    });

    return customerData.data;
  } catch {}

  return undefined;
};

export const getCustomerSSN = async (dataOwnerCustomerId: number): Promise<string> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  const personalId: AxiosResponse<string> = await API({
    method: 'post',
    url: `/v1/getSSN/${dataOwnerCustomerId}`,
    headers: {
      Authorization: `Bearer ${bearer}`,
    },
    data: { content },
  }).catch((error) => {
    if (error.response) {
      if (error.response.status === 401) {
        logUserOut();
      }
      return error.response;
    }
  });

  return personalId.data;
};

export const exoveSubscribe = async (customerId: number): Promise<number> => {
  const subscribe: AxiosResponse<number> = await API({
    method: 'post',
    url: `/subscribe/${customerId}`,
  }).catch();

  return subscribe.data;
};

export const logout = async (): Promise<number | undefined> => {
  const { bearer } = getUserToken();

  if (bearer) {
    const logout: AxiosResponse<number> = await API({
      method: 'post',
      url: `/v1/logout`,
      headers: {
        Authorization: `Bearer ${bearer}`,
      },
    }).catch();

    return logout.status;
  }
};
